import {
  SUDOKU_CELL_COUNT,
  SUDOKU_DIGITS,
  SUDOKU_SIZE,
  type SudokuDigit,
  type SudokuValue,
} from './types';

export const SUDOKU_UNITS = buildUnits();

function buildUnits(): number[][] {
  const units: number[][] = [];
  for (let row = 0; row < SUDOKU_SIZE; row += 1) {
    units.push(Array.from({ length: SUDOKU_SIZE }, (_, column) => row * SUDOKU_SIZE + column));
  }
  for (let column = 0; column < SUDOKU_SIZE; column += 1) {
    units.push(Array.from({ length: SUDOKU_SIZE }, (_, row) => row * SUDOKU_SIZE + column));
  }
  for (let boxRow = 0; boxRow < 3; boxRow += 1) {
    for (let boxColumn = 0; boxColumn < 2; boxColumn += 1) {
      const unit: number[] = [];
      for (let rowOffset = 0; rowOffset < 2; rowOffset += 1) {
        for (let columnOffset = 0; columnOffset < 3; columnOffset += 1) {
          const row = boxRow * 2 + rowOffset;
          const column = boxColumn * 3 + columnOffset;
          unit.push(row * SUDOKU_SIZE + column);
        }
      }
      units.push(unit);
    }
  }
  return units;
}

export function getPeers(index: number): number[] {
  return [
    ...new Set(
      SUDOKU_UNITS.filter((unit) => unit.includes(index))
        .flat()
        .filter((cell) => cell !== index),
    ),
  ];
}

export function candidatesFor(board: readonly SudokuValue[], index: number): SudokuDigit[] {
  if (board[index] !== 0) return [];
  const used = new Set(
    getPeers(index)
      .map((peer) => board[peer])
      .filter(Boolean),
  );
  return SUDOKU_DIGITS.filter((digit) => !used.has(digit));
}

export function findConflictIndices(board: readonly SudokuValue[]): Set<number> {
  const conflicts = new Set<number>();
  for (const unit of SUDOKU_UNITS) {
    const byValue = new Map<number, number[]>();
    for (const index of unit) {
      const value = board[index];
      if (value === 0) continue;
      const matches = byValue.get(value) ?? [];
      matches.push(index);
      byValue.set(value, matches);
    }
    for (const matches of byValue.values()) {
      if (matches.length > 1) matches.forEach((index) => conflicts.add(index));
    }
  }
  return conflicts;
}

export function placementWarning(
  board: readonly SudokuValue[],
  index: number,
  value: SudokuDigit,
): string | null {
  const row = Math.floor(index / SUDOKU_SIZE);
  const column = index % SUDOKU_SIZE;
  const boxRow = Math.floor(row / 2);
  const boxColumn = Math.floor(column / 3);
  const locations: string[] = [];
  if (
    board.some(
      (cell, cellIndex) =>
        cell === value && Math.floor(cellIndex / 6) === row && cellIndex !== index,
    )
  ) {
    locations.push('row');
  }
  if (
    board.some(
      (cell, cellIndex) => cell === value && cellIndex % 6 === column && cellIndex !== index,
    )
  ) {
    locations.push('column');
  }
  if (
    board.some((cell, cellIndex) => {
      const cellRow = Math.floor(cellIndex / 6);
      const cellColumn = cellIndex % 6;
      return (
        cell === value &&
        Math.floor(cellRow / 2) === boxRow &&
        Math.floor(cellColumn / 3) === boxColumn &&
        cellIndex !== index
      );
    })
  ) {
    locations.push('box');
  }
  if (locations.length === 0) return null;
  return `${value} already appears in this ${locations.join(' and ')}`;
}

export function countSudokuSolutions(boardValue: readonly SudokuValue[], limit = 2): number {
  const board = [...boardValue];
  let solutions = 0;

  function search() {
    if (solutions >= limit) return;
    let target = -1;
    let targetCandidates: SudokuDigit[] = [];
    for (let index = 0; index < SUDOKU_CELL_COUNT; index += 1) {
      if (board[index] !== 0) continue;
      const candidates = candidatesFor(board, index);
      if (candidates.length === 0) return;
      if (target === -1 || candidates.length < targetCandidates.length) {
        target = index;
        targetCandidates = candidates;
        if (candidates.length === 1) break;
      }
    }
    if (target === -1) {
      solutions += 1;
      return;
    }
    for (const digit of targetCandidates) {
      board[target] = digit;
      search();
      board[target] = 0;
      if (solutions >= limit) return;
    }
  }

  if (findConflictIndices(board).size > 0) return 0;
  search();
  return solutions;
}

export type SudokuDifficulty = {
  solvedBySingles: boolean;
  rounds: number;
  placements: number;
};

export function rateSudokuDifficulty(boardValue: readonly SudokuValue[]): SudokuDifficulty {
  const board = [...boardValue];
  let rounds = 0;
  let placements = 0;
  let contradictory = findConflictIndices(board).size > 0;

  while (board.includes(0) && !contradictory) {
    const pending = new Map<number, SudokuDigit>();
    const queue = (index: number, digit: SudokuDigit) => {
      const previous = pending.get(index);
      if (previous !== undefined && previous !== digit) contradictory = true;
      pending.set(index, digit);
    };
    for (let index = 0; index < SUDOKU_CELL_COUNT; index += 1) {
      const candidates = candidatesFor(board, index);
      if (board[index] === 0 && candidates.length === 1) queue(index, candidates[0]);
    }
    for (const unit of SUDOKU_UNITS) {
      for (const digit of SUDOKU_DIGITS) {
        if (unit.some((index) => board[index] === digit)) continue;
        const positions = unit.filter(
          (index) => board[index] === 0 && candidatesFor(board, index).includes(digit),
        );
        if (positions.length === 1) queue(positions[0], digit);
      }
    }
    if (pending.size === 0 || contradictory) break;
    for (const [index, digit] of pending) board[index] = digit;
    contradictory = findConflictIndices(board).size > 0;
    placements += pending.size;
    rounds += 1;
  }

  return { solvedBySingles: !contradictory && !board.includes(0), rounds, placements };
}

export function isCompleteSudoku(board: readonly SudokuValue[]): boolean {
  return (
    board.length === SUDOKU_CELL_COUNT &&
    !board.includes(0) &&
    findConflictIndices(board).size === 0
  );
}
